import { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate, useLocation, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/common";
import { useT, LanguageSelector } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { MobileBottomNavigation } from "@/components/dispatch/DispatchUI";
import { InstallAppButton } from "@/components/InstallAppButton";
import { ensurePushSubscription, removePushSubscription } from "@/lib/push";
import { AskErp } from "@/components/AskErp";
import { toast } from "sonner";
import {
  SquaresFour, Package, Truck, Receipt, Users, Buildings, Factory,
  ClipboardText, SignOut, List, X, Plus, DownloadSimple, Megaphone, Pill,
  Bell, CheckCircle, PaperPlaneTilt, SquareSplitHorizontal, ArrowsLeftRight,
  Gear, ArrowsClockwise, AirplaneTilt, Stack, CalendarBlank, FileText, Sun, Moon, DotsThree, ChartBar, Lifebuoy, ChatCircleText,
} from "@phosphor-icons/react";

function timeAgo(ts) {
  const d = new Date(ts);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function NotificationBell() {
  const { t } = useT();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const lastSeen = useRef(null);
  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      const items = data.items || [];
      setItems(items);
      setUnread(data.unread || 0);
      const fresh = lastSeen.current ? items.filter((n) => !n.read && n.ts > lastSeen.current) : [];
      if (items[0]?.ts) lastSeen.current = items[0].ts > (lastSeen.current || "") ? items[0].ts : lastSeen.current;
      if (fresh.length && typeof Notification !== "undefined" && Notification.permission === "granted") {
        const reg = await navigator.serviceWorker?.getRegistration();
        fresh.slice(0, 3).forEach((n) => {
          const opts = { body: n.body || "", icon: "/icon-192.png", badge: "/icon-192.png", tag: n.id, data: { link: n.link || "/" } };
          if (reg?.showNotification) reg.showNotification(n.title, opts); else new Notification(n.title, opts);
        });
      }
    } catch (_) {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    const onVisible = () => document.visibilityState === "visible" && load();
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVisible); };
  }, []);

  const askPermission = () => {
    if (typeof Notification === "undefined") return;
    if (Notification.permission === "default") Notification.requestPermission().then((p) => p === "granted" && ensurePushSubscription());
    else if (Notification.permission === "granted") ensurePushSubscription();
  };
  useEffect(() => { ensurePushSubscription(); }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const openItem = async (n) => {
    setOpen(false);
    if (!n.read) {
      await api.post(`/notifications/${n.id}/read`).catch(() => {});
      load();
    }
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    load();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => { askPermission(); setOpen((o) => !o); }}
        data-testid="notifications-bell"
        aria-label={unread > 0 ? `${unread} unread notifications` : "Notifications"}
        title="Notifications"
        className="relative flex h-9 w-9 items-center justify-center rounded-full text-foreground/70 hover:text-primary hover:bg-primary/5 transition-colors"
      >
        <Bell size={20} weight="duotone" />
        {unread > 0 && (
          <span
            data-testid="notifications-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[hsl(43_91%_50%)] text-[hsl(213_66%_16%)] text-[10px] font-bold flex items-center justify-center"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid="notifications-dropdown"
          className="fixed top-14 right-3 w-[min(24rem,calc(100vw-1.5rem))] max-h-[70vh] overflow-y-auto rounded-lg border border-border bg-card shadow-xl z-50"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-border sticky top-0 bg-card">
            <p className="font-heading font-semibold text-sm">{t("topbar.notifications")}</p>
            {unread > 0 && (
              <button onClick={markAll} data-testid="notifications-mark-all" className="text-xs text-primary hover:underline flex items-center gap-1">
                <CheckCircle size={14} weight="fill" /> {t("topbar.markAll")}
              </button>
            )}
          </div>
          {typeof Notification !== "undefined" && Notification.permission !== "granted" && (
            <button onClick={askPermission} data-testid="enable-push-alerts" className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-left bg-primary/5 text-primary border-b border-border hover:bg-primary/10">
              <Bell size={14} weight="fill" /> {Notification.permission === "denied" ? "Device alerts are blocked in your browser settings" : "Enable device alerts on this phone/computer"}
            </button>
          )}
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground" data-testid="notifications-empty">
              {t("topbar.caughtUp")}
            </div>
          ) : (
            items.map((n) => (
              <button
                key={n.id}
                onClick={() => openItem(n)}
                data-testid={`notification-item-${n.id}`}
                className={`w-full text-left px-4 py-3 border-b border-border/60 hover:bg-primary/5 transition-colors ${n.read ? "" : "bg-primary/[0.04]"}`}
              >
                <div className="flex items-start gap-2">
                  {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-[hsl(43_91%_50%)] shrink-0" />}
                  <div className={`min-w-0 flex-1 ${n.read ? "pl-4" : ""}`}>
                    <p className="text-sm font-medium truncate">{n.title}</p>
                    {n.body && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</p>}
                    <p className="text-[11px] text-muted-foreground/70 mt-1">{timeAgo(n.ts)}</p>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function PortalMessages() {
  const [messages, setMessages] = useState([]);
  useEffect(() => {
    api.get("/portal-messages").then((r) => setMessages(r.data.messages || [])).catch(() => {});
  }, []);
  if (!messages.length) return null;
  return (
    <>
      {messages.map((m, i) => (
        <div
          key={i}
          data-testid={`portal-message-${m.scope}`}
          className={`relative flex items-start gap-3 px-4 sm:px-8 py-2.5 text-sm text-white ${
            m.scope === "client"
              ? "bg-gradient-to-r from-[hsl(213_66%_20%)] to-[hsl(213_66%_28%)]"
              : "bg-gradient-to-r from-[hsl(210_76%_42%)] via-[hsl(210_76%_47%)] to-[hsl(43_91%_45%)]"
          }`}
        >
          <Megaphone size={18} weight="fill" className="shrink-0 mt-0.5" />
          <p className="flex-1 leading-snug whitespace-pre-wrap">{m.text}</p>
        </div>
      ))}
    </>
  );
}

function navItems(isTeam, role) {
  if (role === "client_ops") {
    return [
      { to: "/portal", label: "Dashboard", tKey: "cops.nav.dashboard", icon: SquaresFour, end: true },
      { to: "/portal/shipments", label: "Shipments", tKey: "cops.nav.shipments", icon: Truck },
      { to: "/portal/approvals", label: "Approvals", tKey: "cops.nav.approvals", icon: CheckCircle },
    ];
  }
  if (role === "dispatch") {
    return [
      { to: "/dispatch", label: "Dispatch Jobs", icon: PaperPlaneTilt, end: true },
      { to: "/dispatch-requests", label: "Booking Requests", icon: AirplaneTilt },
      { to: "/dispatch-invoices", label: "Dispatch Invoices", icon: Receipt },
      { to: "/stock-intake", label: "Stock Intake", icon: Stack },
    ];
  }
  if (isTeam) {
    const items = [
      { to: "/dashboard", label: "Dashboard", icon: SquaresFour, end: true, group: "Overview" },
      { to: "/reports", label: "Reports", icon: ChartBar, group: "Overview" },
      { to: "/orders", label: "Orders", icon: Package, group: "Sales" },
      { to: "/orders/new", label: "New Order", icon: Plus, group: "Sales" },
      { to: "/quotes", label: "Quotes", icon: FileText, group: "Sales" },
      { to: "/invoices", label: "Invoices", icon: Receipt, group: "Sales" },
      { to: "/clients", label: "Clients", icon: Buildings, group: "Sales" },
      { to: "/enquiries", label: "Website Enquiries", icon: ChatCircleText, group: "Sales" },
      { to: "/shipments", label: "Shipments", icon: Truck, group: "Logistics" },
      { to: "/schedule", label: "Schedule", icon: CalendarBlank, group: "Logistics" },
      { to: "/client-confirmations", label: "Client Confirmations", icon: CheckCircle, group: "Logistics" },
      { to: "/external-awbs", label: "External AWBs", icon: FileText, group: "Logistics" },
      { to: "/transit-shipments", label: "Transit Shipments", icon: ArrowsClockwise, group: "Logistics" },
      { to: "/dispatch", label: "Dispatch", icon: PaperPlaneTilt, group: "Logistics" },
      { to: "/dispatch-requests", label: "Booking Requests", icon: AirplaneTilt, group: "Logistics" },
      { to: "/awb-requests", label: "AWB Requests", icon: FileText, group: "Logistics" },
      { to: "/box-labels", label: "Box Labels", icon: Package, group: "Logistics" },
      { to: "/dispatch-invoices", label: "Dispatch Invoices", icon: Receipt, group: "Logistics" },
      { to: "/purchase-orders", label: "Procurement", icon: ClipboardText, group: "Supply" },
      { to: "/stock", label: "Inventory", icon: Stack, group: "Supply" },
      { to: "/catalogue", label: "Products", icon: Pill, group: "Supply" },
      { to: "/suppliers", label: "Suppliers", icon: Factory, group: "Supply" },
    ];
    if (role === "admin") {
      items.push({ to: "/users", label: "Users", icon: Users, group: "Admin" });
      items.push({ to: "/backups", label: "Backups", icon: DownloadSimple, group: "Admin" });
      items.push({ to: "/settings", label: "Settings", icon: Gear, group: "Admin" });
    }
    return items;
  }
  return [
    { to: "/dashboard", label: "Dashboard", tKey: "nav.dashboard", icon: SquaresFour, end: true },
    { to: "/orders/new", label: "Place Order", tKey: "nav.placeOrder", icon: Plus },
    { to: "/orders", label: "My Orders", tKey: "nav.myOrders", icon: Package },
    { to: "/catalogue", label: "Catalogue", tKey: "nav.catalogue", icon: Pill },
    { to: "/shipments", label: "Shipments", tKey: "nav.shipments", icon: Truck },
    { to: "/invoices", label: "Invoices", tKey: "nav.invoices", icon: Receipt },
  ];
}

function ThemeToggle() {
  const [dark, setDark] = useState(() => document.documentElement.classList.contains("dark"));
  const toggle = () => {
    const isDark = document.documentElement.classList.toggle("dark");
    localStorage.setItem("heliopolis-theme", isDark ? "dark" : "light");
    setDark(isDark);
  };
  return (
    <button onClick={toggle} data-testid="theme-toggle" title={dark ? "Switch to light mode" : "Switch to dark mode"} aria-label="Toggle dark mode"
      className="flex h-9 w-9 items-center justify-center rounded-full text-foreground/70 hover:text-primary hover:bg-primary/5 transition-colors">
      {dark ? <Sun size={20} weight="duotone" /> : <Moon size={20} weight="duotone" />}
    </button>
  );
}

export default function AppShell() {
  const { user, logout, isTeam, isDispatch } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => (localStorage.getItem("sidebar_collapsed") ?? "1") === "1");
  const toggleCollapsed = () => setCollapsed((c) => { localStorage.setItem("sidebar_collapsed", c ? "0" : "1"); return !c; });
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showAd, setShowAd] = useState(() => {
    if (localStorage.getItem("expopharm_dismissed") === "1") return false;
    return new Date() < new Date("2026-09-17T23:59:59");
  });
  const items = navItems(isTeam, user?.role);

  // ---- In-app open tabs (Team & Client portals; session-only, reset on login) ----
  const DETAIL_LABELS = [
    ["/orders/new", "Place Order"], ["/orders/", "Order"], ["/shipments/", "Shipment"],
    ["/invoices/", "Invoice"], ["/purchase-orders/", "Purchase Order"], ["/dispatch/", "Dispatch"], ["/quotes/", "Quote"],
  ];
  const labelForPath = (path) => {
    const exact = items.find((it) => it.to === path);
    if (exact) return isTeam ? exact.label : (exact.tKey ? t(exact.tKey) : exact.label);
    if (path === "/" || path === "/dashboard") return isTeam ? "Dashboard" : t("nav.dashboard");
    for (const [prefix, lbl] of DETAIL_LABELS) {
      if (path === prefix || path.startsWith(prefix)) return lbl;
    }
    const seg = path.split("/").filter(Boolean)[0] || "Home";
    return seg.charAt(0).toUpperCase() + seg.slice(1).replace(/-/g, " ");
  };
  const tabId = (path) => (path === "/" || path === "/dashboard" ? "home" : path.replace(/^\//, "").replace(/\//g, "-"));
  const isPane = window.self !== window.top;
  const showTabs = !isDispatch && !isPane && user?.role !== "client_ops";
  // ---- Split view: a second tab rendered side-by-side in a right pane (iframe) ----
  const [splitPath, setSplitPath] = useState(() => sessionStorage.getItem("split_pane") || "");
  const [splitPct, setSplitPct] = useState(() => Number(localStorage.getItem("split_pct")) || 50);
  useEffect(() => { if (splitPath) sessionStorage.setItem("split_pane", splitPath); else sessionStorage.removeItem("split_pane"); }, [splitPath]);
  const splitRef = useRef(null);
  const startResize = (e) => {
    e.preventDefault();
    const box = splitRef.current?.getBoundingClientRect();
    if (!box) return;
    const move = (ev) => {
      const x = (ev.touches ? ev.touches[0].clientX : ev.clientX) - box.left;
      setSplitPct(Math.min(75, Math.max(25, (x / box.width) * 100)));
    };
    const stop = () => {
      document.removeEventListener("mousemove", move); document.removeEventListener("mouseup", stop);
      document.removeEventListener("touchmove", move); document.removeEventListener("touchend", stop);
      setSplitPct((p) => { localStorage.setItem("split_pct", String(Math.round(p))); return p; });
    };
    document.addEventListener("mousemove", move); document.addEventListener("mouseup", stop);
    document.addEventListener("touchmove", move); document.addEventListener("touchend", stop);
  };
  const swapPanes = () => {
    const main = location.pathname;
    navigate(splitPath);
    setSplitPath(main);
  };
  const openInSplit = (e, path) => {
    e.stopPropagation();
    if (location.pathname === path) {
      const other = tabs.find((tp) => tp.path !== path);
      if (!other) return toast.info("Open another tab first, then split it");
      navigate(other.path);
    }
    setSplitPath(path);
  };
  const [tabs, setTabs] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("open_tabs")) || []; } catch { return []; }
  });
  useEffect(() => { sessionStorage.setItem("open_tabs", JSON.stringify(tabs)); }, [tabs]);
  useEffect(() => {
    if (!showTabs) return;
    const path = location.pathname;
    setTabs((prev) => (prev.some((tp) => tp.path === path) ? prev : [...prev, { path, label: labelForPath(path) }]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, showTabs]);
  const closeTab = (e, path) => {
    e.stopPropagation();
    const idx = tabs.findIndex((tp) => tp.path === path);
    const next = tabs.filter((tp) => tp.path !== path);
    setTabs(next);
    if (location.pathname === path) {
      const fallback = next[idx - 1] || next[idx] || next[next.length - 1];
      navigate(fallback ? fallback.path : "/dashboard");
    }
  };
  const [confirmCount, setConfirmCount] = useState(0);
  const [pendingAwb, setPendingAwb] = useState([]);
  const [awbPopup, setAwbPopup] = useState(false);
  useEffect(() => {
    if (!user || user.role !== "client" || isPane) return;
    api.get("/shipments-pending-awb").then((r) => {
      const list = r.data || [];
      setPendingAwb(list);
      if (list.length && sessionStorage.getItem("awb_reminder_shown") !== "1") {
        sessionStorage.setItem("awb_reminder_shown", "1");
        setAwbPopup(true);
      }
    }).catch(() => {});
  }, [user]);
  useEffect(() => {
    if (!isTeam) return;
    const refresh = () => {
      const since = localStorage.getItem("confirmations_last_seen") || "";
      api.get(`/shipments-confirmed/count${since ? `?since=${encodeURIComponent(since)}` : ""}`)
        .then((r) => setConfirmCount(r.data?.count || 0)).catch(() => {});
    };
    refresh();
    window.addEventListener("confirmations-seen", refresh);
    return () => window.removeEventListener("confirmations-seen", refresh);
  }, [isTeam]);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const [iosHelp, setIosHelp] = useState(false);
  const installApp = async () => {
    if (!deferredPrompt) {
      if (isIOS) { setIosHelp(true); return; }
      toast.info("To install: open your browser menu and choose “Install app”.");
      return;
    }
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
  };

  const doLogout = async () => {
    await removePushSubscription();
    logout();
    navigate("/login");
  };

  const SidebarBody = ({ prefix = "", rail = false }) => {
    let lastGroup = null;
    return (
    <div className="flex h-full flex-col">
      <div className={`flex items-center ${rail ? "justify-center px-2" : "justify-between px-4"} h-20 border-b border-border/60`}>
        {rail ? <Logo variant="mark" className="h-11 w-11 object-contain" /> : <Logo className="h-12" />}
        <button className={`${rail ? "hidden" : "lg:hidden"} text-muted-foreground hover:text-foreground`} onClick={() => setOpen(false)} data-testid={`${prefix}close-sidebar`}>
          <X size={22} />
        </button>
      </div>
      {!rail && (
        <div className="px-5 py-3 border-b border-border/60">
          <p className="text-[11px] uppercase tracking-[0.2em] font-semibold text-muted-foreground">
            {isDispatch ? "Dispatch Portal" : isTeam ? "Team Portal" : user?.role === "client_ops" ? t("cops.portal") : t("portal.client")}
          </p>
        </div>
      )}
      <nav className={`flex-1 overflow-y-auto py-3 ${rail ? "px-2 space-y-1" : "px-3 space-y-0.5"}`}>
        {items.map((it) => {
          const showGroup = !rail && it.group && it.group !== lastGroup;
          lastGroup = it.group || lastGroup;
          const label = isTeam || isDispatch ? it.label : (it.tKey ? t(it.tKey) : it.label);
          return (
          <div key={it.to}>
            {showGroup && <p className="px-3 pt-4 pb-1.5 text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground/70">{it.group}</p>}
            <NavLink
              to={it.to}
              end={it.end}
              onClick={() => setOpen(false)}
              title={rail ? label : undefined}
              data-testid={`${prefix}nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `group/nav relative flex items-center gap-3 rounded-xl text-sm font-medium transition-colors ${rail ? "justify-center h-11 w-11 mx-auto" : "px-3 py-2.5"} ${
                  isActive ? "bg-[#377DD3]/10 text-[#245FAF] dark:text-primary" : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`
              }
            >
              {({ isActive }) => (<>
                {isActive && !rail && <span className="absolute left-0 top-2 bottom-2 w-[3px] rounded-full bg-[#377DD3]" />}
                <it.icon size={rail ? 22 : 18} weight="duotone" className="shrink-0" />
                {!rail && <span className="flex-1 truncate">{label}</span>}
                {rail && <span className="hp-rail-tip">{label}</span>}
                {it.to === "/client-confirmations" && confirmCount > 0 && (
                  <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[hsl(var(--brand-gold))] text-[hsl(213_66%_18%)] text-[10px] font-bold ${rail ? "absolute -top-0.5 -right-0.5" : "ml-auto"}`} data-testid="confirmations-badge">{confirmCount}</span>
                )}
              </>)}
            </NavLink>
          </div>);
        })}
      </nav>
      <div className={`border-t border-border/60 shrink-0 ${rail ? "p-2" : "p-4"}`} style={rail ? undefined : { paddingBottom: "calc(env(safe-area-inset-bottom) + 1rem)" }}>
        {!rail && (
          <a href="mailto:ops@heliopolispharmaceuticals.com" className="mb-3 flex items-start gap-2.5 rounded-xl border border-[#377DD3]/15 bg-[#EAF2F8]/70 dark:bg-muted p-3 text-xs hover:border-[#377DD3]/40 transition-colors" data-testid="sidebar-help-card">
            <Lifebuoy size={16} className="text-[#377DD3] shrink-0 mt-0.5" />
            <span><span className="block font-semibold text-foreground">Need help?</span><span className="text-muted-foreground break-all">ops@heliopolispharmaceuticals.com</span></span>
          </a>
        )}
        <div className={`flex items-center gap-3 ${rail ? "justify-center" : "mb-3"}`}>
          <div className="h-9 w-9 rounded-full bg-[hsl(var(--brand-gold))] flex items-center justify-center font-heading font-bold text-[hsl(213_66%_16%)] text-sm shrink-0" title={user?.email}>
            {user?.name?.[0]?.toUpperCase() || "U"}
          </div>
          {!rail && (
            <div className="min-w-0">
              <p className="text-sm font-medium truncate text-foreground">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.role}</p>
            </div>
          )}
        </div>
        {rail ? (
          <button onClick={doLogout} data-testid={`${prefix}logout-button`} title={t("topbar.signout")} className="group/nav relative mt-2 mx-auto flex h-10 w-10 items-center justify-center rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"><SignOut size={20} /><span className="hp-rail-tip">{t("topbar.signout")}</span></button>
        ) : (
          <>
            <InstallAppButton className="mb-2" />
            <Button variant="outline" size="sm" className="w-full rounded-xl" onClick={doLogout} data-testid={`${prefix}logout-button`}>
              <SignOut size={16} className="mr-2" /> {t("topbar.signout")}
            </Button>
          </>
        )}
      </div>
    </div>
  ); };

  const MAX_TABS = 5;
  const visibleTabs = tabs.length > MAX_TABS ? tabs.slice(-MAX_TABS) : tabs;
  const overflowTabs = tabs.length > MAX_TABS ? tabs.slice(0, tabs.length - MAX_TABS) : [];
  const [moreOpen, setMoreOpen] = useState(false);
  const closeOthers = () => { setTabs(tabs.filter((tp) => tp.path === location.pathname)); setMoreOpen(false); };

  return isPane ? (
    <main className="h-screen overflow-y-auto hp-main" data-testid="pane-main">
      <div className="hp-content mx-auto px-5 sm:px-6 py-6">
        <Outlet />
      </div>
    </main>
  ) : (
    <div className="flex h-screen overflow-hidden">
      <aside className={`hidden lg:block ${collapsed ? "w-[76px]" : "w-64"} shrink-0 border-r border-border/60 hp-sidebar transition-[width] duration-200`} data-testid="sidebar" data-collapsed={collapsed ? "1" : "0"}>
        <SidebarBody rail={collapsed} />
      </aside>

      {open && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-72 hp-sidebar border-r border-border/60 rounded-r-2xl shadow-2xl overflow-hidden" style={{ height: "100dvh" }}>
            <SidebarBody prefix="mobile-" />
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="relative z-30 flex items-center justify-between h-14 px-4 sm:px-6 border-b border-border/60 hp-header backdrop-blur-md">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="open-sidebar">
              <List size={24} />
            </button>
            <button
              className="hidden lg:inline-flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              onClick={toggleCollapsed}
              title={collapsed ? "Expand sidebar" : "Compact sidebar"}
              aria-label={collapsed ? "Expand sidebar" : "Compact sidebar"}
              data-testid="toggle-sidebar"
            >
              <List size={22} />
            </button>
            <Logo className="h-9 lg:hidden" />
          </div>
          <div className="flex items-center gap-1 min-w-0">
            {!isTeam && !isDispatch && <LanguageSelector className="mr-1" />}
            {isTeam && <AskErp />}
            <ThemeToggle />
            <NotificationBell />
            <button
              className="hidden sm:flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              onClick={() => window.location.reload()}
              title="Refresh app"
              aria-label="Refresh app"
              data-testid="refresh-app-button"
            >
              <ArrowsClockwise size={20} weight="duotone" />
            </button>
            <Button
              variant="outline"
              size="sm"
              className="rounded-full border-primary/40 text-primary hover:bg-primary/5 shrink-0 max-sm:h-9 max-sm:w-9 max-sm:p-0"
              onClick={installApp}
              title={t("topbar.download")}
              aria-label={t("topbar.download")}
              data-testid="install-app-button"
            >
              <DownloadSimple size={16} className="sm:mr-2" /> <span className="hidden sm:inline">{t("topbar.download")}</span>
            </Button>
          </div>
        </header>
        {showAd && (
          <div data-testid="announcement-banner" className="relative flex items-center gap-3 px-4 sm:px-8 py-2.5 text-sm text-white bg-gradient-to-r from-[hsl(210_76%_42%)] via-[hsl(210_76%_47%)] to-[hsl(43_91%_45%)]">
            <Megaphone size={18} weight="fill" className="shrink-0" />
            <p className="flex-1 leading-snug">
              <span className="font-bold">We're exhibiting at EXPOPHARM 2026</span> — Munich, Germany · Hall A3, Booth B64. Come visit us before 17 Sep 2026!
            </p>
            <button onClick={() => { localStorage.setItem("expopharm_dismissed", "1"); setShowAd(false); }} data-testid="dismiss-announcement" className="shrink-0 opacity-80 hover:opacity-100">
              <X size={16} />
            </button>
          </div>
        )}
        {!isTeam && !isDispatch && <PortalMessages />}
        {showTabs && tabs.length > 1 && (
          <div data-testid="tab-strip" className="relative flex items-center gap-1.5 overflow-x-auto border-b border-border/60 bg-card/60 px-3 py-1.5">
            {overflowTabs.length > 0 && (
              <div className="relative shrink-0">
                <button onClick={() => setMoreOpen((o) => !o)} data-testid="tabs-more" className="flex items-center gap-1 rounded-full border border-border/70 bg-card px-2.5 py-1 text-xs font-medium text-muted-foreground hover:text-foreground">
                  <DotsThree size={16} weight="bold" /> {overflowTabs.length} more
                </button>
                {moreOpen && (
                  <div className="absolute left-0 top-9 z-50 w-60 rounded-xl border border-border/70 bg-popover shadow-xl p-1" data-testid="tabs-more-menu">
                    {overflowTabs.map((tp) => (
                      <div key={tp.path} className="flex items-center justify-between rounded-lg px-2.5 py-1.5 text-sm hover:bg-muted cursor-pointer" onClick={() => { setMoreOpen(false); navigate(tp.path); }}>
                        <span className="truncate">{tp.label}</span>
                        <button onClick={(e) => closeTab(e, tp.path)} className="text-muted-foreground hover:text-red-600" aria-label={`Close ${tp.label}`}><X size={13} /></button>
                      </div>
                    ))}
                    <button onClick={closeOthers} className="mt-1 w-full rounded-lg px-2.5 py-1.5 text-left text-xs text-muted-foreground hover:bg-muted" data-testid="tabs-close-others">Close all other tabs</button>
                  </div>
                )}
              </div>
            )}
            {visibleTabs.map((tp) => {
              const active = location.pathname === tp.path;
              return (
                <div
                  key={tp.path}
                  onClick={() => navigate(tp.path)}
                  data-testid={`tab-${tabId(tp.path)}`}
                  className={`group flex items-center gap-2 shrink-0 cursor-pointer rounded-full px-3 py-1 text-sm border transition-colors ${active ? "bg-primary/10 border-primary/30 text-primary font-medium" : "border-transparent text-muted-foreground hover:bg-muted hover:text-foreground"}`}
                >
                  <span className="whitespace-nowrap">{tp.label}</span>
                  {!active && (
                    <button
                      onClick={(e) => openInSplit(e, tp.path)}
                      data-testid={`tab-split-${tabId(tp.path)}`}
                      className="hidden lg:inline-flex opacity-50 group-hover:opacity-100 hover:text-primary"
                      aria-label={`Open ${tp.label} side by side`}
                      title="Open side by side"
                    >
                      <SquareSplitHorizontal size={14} />
                    </button>
                  )}
                  <button
                    onClick={(e) => closeTab(e, tp.path)}
                    data-testid={`tab-close-${tabId(tp.path)}`}
                    className="opacity-50 group-hover:opacity-100 hover:text-red-600"
                    aria-label={`Close ${tp.label}`}
                  >
                    <X size={13} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
        <div ref={splitRef} className="flex-1 flex overflow-hidden">
          <main className="flex-1 overflow-y-auto overflow-x-hidden hp-main" style={splitPath ? { flex: `0 0 ${splitPct}%` } : undefined}>
            <div className="hp-content mx-auto px-4 sm:px-8 py-5 sm:py-8 pb-28 sm:pb-8">
              <Outlet />
            </div>
          </main>
          {(isTeam || isDispatch || user?.role === "client" || user?.role === "client_ops") && !splitPath && (
            <MobileBottomNavigation isDispatch={isDispatch} isTeam={isTeam} isClientOps={user?.role === "client_ops"} onMenu={() => setOpen(true)} onNotifications={() => document.querySelector('[data-testid="notifications-bell"]')?.click()} />
          )}
          {splitPath && (
            <>
              <div
                onMouseDown={startResize}
                onTouchStart={startResize}
                className="hidden lg:block w-1.5 shrink-0 cursor-col-resize bg-border hover:bg-primary/60 transition-colors"
                data-testid="split-divider"
                role="separator"
                aria-orientation="vertical"
              />
              <section className="hidden lg:flex flex-1 min-w-0 flex-col border-l border-border bg-card" data-testid="split-pane">
                <div className="flex items-center gap-2 h-9 px-3 border-b border-border bg-muted/40 text-sm">
                  <SquareSplitHorizontal size={15} className="text-primary" />
                  <span className="font-medium truncate" data-testid="split-pane-label">{labelForPath(splitPath)}</span>
                  <button onClick={swapPanes} className="ml-auto inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" title="Swap left and right" data-testid="split-pane-swap"><ArrowsLeftRight size={14} /> Swap</button>
                  <button onClick={() => { navigate(splitPath); setSplitPath(""); }} className="text-xs text-muted-foreground hover:text-foreground" data-testid="split-pane-focus">Open in main</button>
                  <button onClick={() => setSplitPath("")} className="text-muted-foreground hover:text-red-600" aria-label="Close split view" data-testid="split-pane-close"><X size={15} /></button>
                </div>
                <iframe title="Split view" src={splitPath} className="flex-1 w-full border-0" data-testid="split-pane-frame" />
              </section>
            </>
          )}
        </div>
      </div>
      <Dialog open={awbPopup} onOpenChange={setAwbPopup}>
        <DialogContent className="max-w-md" data-testid="awb-reminder-dialog">
          <DialogHeader><DialogTitle>Airway bill (AWB) awaiting your confirmation</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            {pendingAwb.length === 1 ? "1 shipment has" : `${pendingAwb.length} shipments have`} an AWB attached that you have not confirmed yet. Please review and confirm.
          </p>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {pendingAwb.map((p) => (
              <button
                key={p.id}
                onClick={() => { setAwbPopup(false); navigate(`/shipments/${p.id}`); }}
                className="w-full flex items-center justify-between border border-border rounded-sm px-3 py-2 text-sm hover:bg-muted transition-colors"
                data-testid={`awb-reminder-${p.shipment_number}`}
              >
                <span className="font-semibold">{p.shipment_number}</span>
                <span className="text-xs text-primary font-medium">Review &amp; confirm →</span>
              </button>
            ))}
          </div>
          <Button variant="outline" className="rounded-sm w-full" onClick={() => setAwbPopup(false)} data-testid="awb-reminder-later">Remind me later</Button>
        </DialogContent>
      </Dialog>
      <Dialog open={iosHelp} onOpenChange={setIosHelp}>
        <DialogContent className="max-w-sm" data-testid="ios-install-dialog">
          <DialogHeader><DialogTitle>Install on iPhone / iPad</DialogTitle></DialogHeader>
          <ol className="list-decimal pl-5 space-y-2 text-sm">
            <li>Open this page in <strong>Safari</strong>.</li>
            <li>Tap the <strong>Share</strong> button (square with an arrow).</li>
            <li>Scroll and tap <strong>Add to Home Screen</strong>.</li>
            <li>Tap <strong>Add</strong> — the Heliopolis app appears on your home screen.</li>
          </ol>
        </DialogContent>
      </Dialog>
    </div>
  );
}
